import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import {
  IonContent,
  IonIcon,
  IonPage,
  useIonRouter,
  useIonViewDidEnter,
  useIonViewDidLeave,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from "@ionic/react";
import { arrowBack } from "ionicons/icons";
import { useParams } from "react-router-dom";
import { useResonanceLayout } from "../../components/organisms/ResonanceContext/ResonanceLayoutContext";
import { useVideoContextWindow } from "../../hooks/useVideoContextWindow";
import { VideoSwiper } from "../../components/atoms/VideoSwiper";
import { VideoSlide } from "../../components/atoms/VideoSlide";
import { VideoMeta } from "../../services/video.service";
import { useTranslation } from "react-i18next";
import VideoDescriptionPanel from "../Video/VideoDescriptionPanel";
import { useAuth } from "../../contexts/AuthContext";
import { useStudySlug } from "../../hooks/useStudySlug";
import btnSos from "../../assets/MentalPlus/btn_sos.svg";

const SOUND_STORAGE_KEY = "video-muted";
// Pre-white-label key; migrated to SOUND_STORAGE_KEY on first read.
const LEGACY_SOUND_STORAGE_KEY = "mentalplus-video-muted";

const PageWrapper = styled.div`
  position: relative;
  height: 100vh;
  height: 100dvh;
  background: #000;
`;

const TopBar = styled.div`
  position: fixed;
  top: calc(env(safe-area-inset-top, 0px) + 12px);
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px 0 0;
  z-index: 20;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 80px;
  height: 46px;
  padding: 0 6px 0 2px;
  background-color: #ffce48;
  color: #041723;
  border: none;
  border-radius: 0 24px 24px 0;
  box-shadow: 0px 5px 5px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  font-size: 22px;

  @media (min-width: 800px) {
    border-radius: 24px;
  }

  &:active {
    transform: scale(0.97);
  }
`;

const SosButton = styled.button`
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:active {
    transform: scale(0.96);
  }
`;

const SosImg = styled.img`
  width: 48px;
  height: 48px;
  object-fit: contain;
  display: block;
  filter: drop-shadow(0 3px 8px rgba(0, 0, 0, 0.25));
`;

const VideoInfoOverlay = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 20;
  padding: 80px 16px 40px;
  display: flex;
  align-items: flex-end;
  pointer-events: none;
`;

const VideoInfoCard = styled.div`
  /* flex:1 + min-width:0 give the line below a bounded width to ellipsise inside;
     max-width keeps it clear of the right-hand action rail. */
  flex: 1;
  min-width: 0;
  max-width: 78%;
  color: white;
  pointer-events: auto;
`;

/** The whole line is ONE tap target — tapping anywhere on it opens the description sheet. */
const VideoInfoLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  cursor: pointer;

  &:active {
    opacity: 0.75;
  }
`;

const VideoInfoLabel = styled.span`
  /* Shrink-only (never grow): a short title keeps "voir plus" right next to it instead
     of pushing it to the far edge; a long one truncates to the available width. */
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  line-height: 1.5;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.92);
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.4);
`;

const MoreText = styled.span`
  flex: 0 0 auto;
  font-weight: 700;
  color: #ffffff;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.4);
`;

const shareUrlFrom = (hlsUrl: string) => {
  try {
    const u = new URL(hlsUrl);
    const parts = u.pathname.split("/").filter(Boolean);
    const uid = parts[0];
    return `${u.origin}/${uid}/watch`;
  } catch {
    return hlsUrl;
  }
};

const hasExplicationTarget = (vid?: VideoMeta) => {
  const linkedUid = vid?.meta?.linked_uid?.trim();
  const linkedUrl = vid?.meta?.linked_url?.trim();
  return Boolean(linkedUid || linkedUrl);
};

const fullscreenContentStyle = {
  "--offset-top": "0px",
  "--padding-top": "0px",
  "--padding-bottom": "0px",
  "--background": "#000",
} as React.CSSProperties;

const DisplayVideo: React.FC = () => {
  const [pageActive, setPageActive] = useState(false);
  const [liked, setLiked] = useState(false);
  const [shared, setShared] = useState(false);
  const [savedVideoIds, setSavedVideoIds] = useState<string[]>([]);
  const [muted, setMuted] = useState(false);
  const [descriptionOpen, setDescriptionOpen] = useState(false);

  const { id } = useParams<{ id: string }>();
  const ionRouter = useIonRouter();
  const { setResonanceLayout } = useResonanceLayout();
  const { t } = useTranslation();
  const { user } = useAuth();
  const uid = user?.id ?? "guest";
  const study_slug = useStudySlug();
  const wantPlayRef = useRef(false);

  // Retour direct vers la grille de toutes les vidéos (hors contenu d'île).
  const handleBackToList = () => {
    if (window.location.pathname.includes("/island/")) {
      window.history.back();
    } else {
      // Empêche la grille de relancer automatiquement le lecteur à ce retour.
      sessionStorage.setItem("videos_skip_autolaunch", "1");
      ionRouter.push(`/${study_slug}/tabs/videos`, "back", "pop");
    }
  };

  // Appelle le numéro d'urgence actif de "Ma bouée" (défaut : 3114).
  const callSos = () => {
    let line = "Prévention suicide – 3114";
    try {
      const stored = localStorage.getItem(`${uid}_activeUrgenceLinesV2`);
      const arr = stored ? (JSON.parse(stored) as string[]) : [];
      if (arr.length > 0) line = arr[0];
    } catch {
      /* défaut */
    }
    const number = (line.split("–")[1] ?? "3114").replace(/[^\d+]/g, "");
    window.location.href = `tel:${number}`;
  };

  const urlSearch = window.location.search;

  const searchParams = useMemo(() => {
    return new URLSearchParams(urlSearch);
  }, [urlSearch]);

  const tags = useMemo(() => {
    return (
      searchParams
        .get("tags")
        ?.split(",")
        .map((t) => t.trim())
        .filter(Boolean) ?? []
    );
  }, [searchParams]);

  const searchQuery = useMemo(() => {
    return searchParams.get("search")?.trim() ?? "";
  }, [searchParams]);

  const {
    videos,
    currentIndex,
    setCurrentIndex,
    loadPrev,
    loadNext,
    initialSlide,
    swiperKey,
    initSwiperDoneRef,
    suppressEdgeLoadsRef,
  } = useVideoContextWindow(id, tags, searchQuery);

  const activeVideo = videos[currentIndex];

  // The line above the seek bar: the teaser derived from the authored description, or
  // the video's title when that description opens on something other than a paragraph
  // (a hero/image first). Without the fallback the line — and with it the only way into
  // the description sheet besides the "Plus" button — would silently disappear.
  const overlayLabel =
    activeVideo?.meta?.description?.trim() ||
    activeVideo?.meta?.title?.trim() ||
    activeVideo?.meta?.name?.trim() ||
    "";

  useEffect(() => {
    let savedMuted = localStorage.getItem(SOUND_STORAGE_KEY);

    // One-time migration: carry over the old key's value if the new one is
    // not set yet.
    if (savedMuted === null) {
      const legacyMuted = localStorage.getItem(LEGACY_SOUND_STORAGE_KEY);

      if (legacyMuted !== null) {
        localStorage.setItem(SOUND_STORAGE_KEY, legacyMuted);
        savedMuted = legacyMuted;
      }
    }

    if (savedMuted === null) {
      setMuted(false);
    } else {
      setMuted(savedMuted === "true");
    }
  }, []);

  useEffect(() => {
  const syncSavedVideos = () => {
    const raw = localStorage.getItem("savedVideos");

    if (!raw) {
      setSavedVideoIds([]);
      return;
    }

    try {
      const parsed = JSON.parse(raw);
      const ids = Array.isArray(parsed)
        ? parsed.map((v: any) => (typeof v === "string" ? v : v.uid))
        : [];
      setSavedVideoIds(ids);
    } catch {
      setSavedVideoIds([]);
    }
  };

  syncSavedVideos();

  window.addEventListener("saved-videos-updated", syncSavedVideos);

  return () => {
    window.removeEventListener("saved-videos-updated", syncSavedVideos);
  };
}, []);
  const handleToggleMute = () => {
    setMuted((prev) => {
      const next = !prev;
      localStorage.setItem(SOUND_STORAGE_KEY, String(next));
      return next;
    });
  };

  const handleToggleSave = (vid: any) => {
  const raw = localStorage.getItem("savedVideos");
  const current = raw ? JSON.parse(raw) : [];

  const exists = current.some((v: any) => v.uid === vid.uid);

  const next = exists
    ? current.filter((v: any) => v.uid !== vid.uid)
    : [
        ...current,
        {
          uid: vid.uid,
          hlsUrl: vid.hlsUrl,
          thumbnailUrl: vid.thumbnailUrl,
          meta: vid.meta,
        },
      ];

  localStorage.setItem("savedVideos", JSON.stringify(next));
  setSavedVideoIds(next.map((v: any) => v.uid));

  window.dispatchEvent(new Event("saved-videos-updated"));
};
  const handleShowExplication = (vid: VideoMeta) => {
    const linkedUid = vid.meta?.linked_uid?.trim();
    const linkedUrl = vid.meta?.linked_url?.trim();

    if (linkedUid) {
      const url = new URL(window.location.href);
      const parts = url.pathname.split("/").filter(Boolean);

      if (parts.length >= 2) {
        parts[parts.length - 2] = linkedUid;
      }

      const newPath = "/" + parts.join("/");
      const newUrl = newPath + url.search;

      ionRouter.push(newUrl, "forward", "push");
      return;
    }

    if (linkedUrl) {
      window.open(linkedUrl, "_blank", "noopener,noreferrer");
    }
  };

  // The player is full-bleed: no tab bar over the video. Asserted per VIEW (not per
  // mount) and only ever as "hide".
  //
  // This used to be a raw `document.getElementById("ion-tab-bar").style.display` write in
  // a mount-scoped effect, which broke two ways. Ionic keeps pages MOUNTED in the router
  // outlet, so hopping player → player (an "explication" link, a content card, or just
  // back and forth) leaves several DisplayVideo instances alive; the first one to unmount
  // ran the cleanup and forced the bar back to `display: flex` on top of the player that
  // was still on screen and playing. And the bar is rendered by React via
  // `{!disableNav && <IonTabBar/>}` (navigation/Tabs.tsx), so an inline style was fighting
  // the framework over the same element anyway.
  useIonViewWillEnter(() => {
    setResonanceLayout({ disableNav: true });
  });

  useIonViewDidEnter(() => {
    setPageActive(true);
    wantPlayRef.current = true;
  });

  // Hand the tab bar back on WILL-leave, never on DID-leave. Ionic core fires
  // `willLeave(leaving)` BEFORE `willEnter(entering)`, but `didLeave(leaving)` AFTER
  // `didEnter(entering)` (fireWillEvents / fireDidEvents in @ionic/core). So:
  //   player → player : this false lands first, the next player's willEnter re-asserts
  //                     true — the bar never flashes over the video.
  //   player → page   : this false lands and stays, even for a host page that never
  //                     touches disableNav itself.
  // Doing it in didLeave — as this page used to — reverses that and is the race.
  useIonViewWillLeave(() => {
    setResonanceLayout({ disableNav: false });
  });

  useIonViewDidLeave(() => {
    setPageActive(false);
    wantPlayRef.current = false;
  });

  return (
    <IonPage>
      <IonContent scrollY={false} fullscreen style={fullscreenContentStyle}>
        <PageWrapper>
          <TopBar>
            <BackButton onClick={handleBackToList} aria-label="Retour aux vidéos">
              <IonIcon icon={arrowBack} />
            </BackButton>
            <SosButton onClick={callSos} aria-label="Appeler le numéro d'urgence">
              <SosImg src={btnSos} alt="SOS" />
            </SosButton>
          </TopBar>

          <VideoSwiper<VideoMeta>
            items={videos}
            index={currentIndex}
            setIndex={(i) => {
              setCurrentIndex(i);

              const nextId = videos[i]?.uid;
              if (nextId && id && nextId !== id) {
                const url = new URL(window.location.href);
                const parts = url.pathname.split("/").filter(Boolean);

                if (parts.length >= 2) {
                  parts[parts.length - 2] = nextId;
                }

                const newPath = "/" + parts.join("/");
                const newUrl = newPath + url.search;
                window.history.replaceState(null, "", newUrl);
              }
            }}
            initialSlide={initialSlide}
            remountKey={swiperKey}
            onNearStart={loadPrev}
            onNearEnd={loadNext}
            initDoneRef={initSwiperDoneRef}
            suppressEdgeLoadsRef={suppressEdgeLoadsRef}
            renderItem={(vid, i) => (
              <VideoSlide
                src={vid.hlsUrl}
                thumbnail={vid.thumbnailUrl}
                active={pageActive && i === currentIndex}
                shouldPlay={pageActive && i === currentIndex}
                muted={muted}
                liked={liked}
                shared={shared}
                saved={savedVideoIds.includes(vid.uid)}
                showExplication={hasExplicationTarget(vid)}
                onToggleLike={() => setLiked((v) => !v)}
                onToggleSave={() => handleToggleSave(vid)}
                onShare={async () => {
                  const url = shareUrlFrom(vid.hlsUrl);
                  await navigator.clipboard.writeText(url);
                  setShared(true);
                  setTimeout(() => setShared(false), 1200);
                }}
                onToggleMute={handleToggleMute}
                onShowExplication={() => handleShowExplication(vid)}
                onMore={() => setDescriptionOpen(true)}
              />
            )}
          />

          {activeVideo && !!overlayLabel && (
            <VideoInfoOverlay>
              <VideoInfoCard>
                <VideoInfoLine
                  role="button"
                  tabIndex={0}
                  onClick={() => setDescriptionOpen(true)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setDescriptionOpen(true);
                    }
                  }}
                >
                  <VideoInfoLabel>{overlayLabel}</VideoInfoLabel>
                  <MoreText>… {t("mentalplus_video_voir_plus")}</MoreText>
                </VideoInfoLine>
              </VideoInfoCard>
            </VideoInfoOverlay>
          )}

          <VideoDescriptionPanel
            isOpen={descriptionOpen}
            onClose={() => setDescriptionOpen(false)}
            video={activeVideo}
          />
        </PageWrapper>
      </IonContent>
    </IonPage>
  );
};

export default DisplayVideo;